/* organization/Workspaces — Organization workspace management.
   Runtime-native: permissions + limits from OrganizationRuntimeContext.
   Live API: orgApi.listWorkspaces with search/status/pagination.
   Lifecycle: Create, Edit, Archive (no Delete — no backend endpoint). */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, Modal, ConfirmDialog } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar, Th } from '../platform/_ui';
import { OrgTemplatesEmbeddable } from './OrgTemplates.jsx';
import {
  useOrgRuntime,
  useOrganization,
  useLimits,
  useUsage,
  useOrgPermissions,
} from './OrganizationRuntimeContext.jsx';
import * as orgApi from '../api/organization.api.js';
import * as templatesApi from '../api/org-workspace-templates.api.js';
import { MODULE_CATALOG, DEFAULT_MODULES } from '../shared/modules.js';

/* L-02/L-03: MODULES constant removed — using DEFAULT_MODULES + MODULE_CATALOG from shared catalog. */

const TIMEZONES = [
  'UTC', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Singapore',
  'Australia/Sydney', 'America/Chicago',
];

const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-IN', label: 'English (IN)' },
  { value: 'en-GB', label: 'English (GB)' },
  { value: 'de-DE', label: 'German'       },
  { value: 'fr-FR', label: 'French'       },
  { value: 'ja-JP', label: 'Japanese'     },
];

const STATUS_OPTS = [
  { value: '',            label: 'All Statuses' },
  { value: 'ACTIVE',      label: 'Active'       },
  { value: 'ONBOARDING',  label: 'Onboarding'   },
  { value: 'ARCHIVED',    label: 'Archived'     },
];

const WS_STATUS_TONE  = { ACTIVE: 'green', ONBOARDING: 'blue', ARCHIVED: 'gray' };
const WS_STATUS_LABEL = { ACTIVE: 'Active', ONBOARDING: 'Onboarding', ARCHIVED: 'Archived' };

const MODULE_TABS = [
  { id: 'workspaces', label: 'Workspaces', icon: 'LayoutGrid' },
  { id: 'templates',  label: 'Templates',  icon: 'Layout'      },
];

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/* ── Generic modal overlay ──────────────────────────────────── */
/* ── Table skeleton ─────────────────────────────────────────── */
function TableSkeleton({ rows = 5 }) {
  return (
    <div className="nx-table-wrap">
      <div className="nx-table-scroll">
        <table className="nx-table">
          <thead><tr>
            <Th icon="LayoutGrid">Workspace</Th>
            <Th icon="Activity">Status</Th>
            <Th icon="Users">Members</Th>
            <Th icon="Grid">Modules</Th>
            <Th icon="Calendar">Created</Th>
            <Th end />
          </tr></thead>
          <tbody>
            {Array.from({ length: rows }).map((_, i) => (
              <tr key={'sk-' + i} className="nx-tr">
                {[160, 60, 30, 100, 80].map((w) => (
                  <td key={w + '-' + i}><div style={{ width: w, height: 14, borderRadius: 4, background: 'var(--nx-border)', opacity: 0.7 }} /></td>
                ))}
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── LimitBanner ────────────────────────────────────────────── */
function LimitBanner({ used, max }) {
  if (max <= 0 || used / max < 0.8) return null;
  const isAtLimit = used >= max;
  const tone = isAtLimit ? 'var(--nx-danger)' : 'var(--nx-warning)';
  const bg   = isAtLimit ? 'var(--nx-danger-tint, #fef2f2)' : 'var(--nx-warning-tint, #fffbeb)';
  const icon = isAtLimit ? 'AlertCircle' : 'AlertTriangle';
  const msg  = isAtLimit
    ? 'Workspace limit reached (' + used + '/' + max + '). Upgrade your plan to create more workspaces.'
    : 'Approaching workspace limit (' + used + '/' + max + '). Consider upgrading.';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 8, background: bg, marginBottom: 14, fontSize: 13 }}>
      <NexusIcon name={icon} size={14} style={{ color: tone }} />
      <span>{msg}</span>
    </div>
  );
}

/* ── Module chip toggle ─────────────────────────────────────── */
function ModuleChip({ m, active, onToggle }) {
  return (
    <button
      type="button"
      className={'nx-chip' + (active ? ' is-active' : '')}
      onClick={() => onToggle(m)}
    >
      <NexusIcon name={active ? 'Check' : 'Plus'} size={11} />{m}
    </button>
  );
}

/* ── Wizard: helpers ─────────────────────────────────────────── */

function initOverrides(tpl) {
  const mods = tpl?.config?.modules?.enabled;
  return { modules: Array.isArray(mods) && mods.length > 0 ? [...mods] : [...DEFAULT_MODULES] };
}

/* ── Wizard: template picker card ──────────────────────────── */

function TemplateCard({ template, isSelected, onSelect }) {
  const isBlank  = template === null;
  const cardName = isBlank ? 'Blank Workspace' : template.name;
  const cardDesc = isBlank
    ? 'Default configuration, no template applied'
    : (template.description || 'No description');
  return (
    <button type="button" aria-pressed={isSelected} onClick={onSelect}
      style={{
        border: `2px solid ${isSelected ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
        borderRadius: 10, padding: 12, cursor: 'pointer', textAlign: 'left',
        background: isSelected ? 'var(--nx-accent-tint)' : 'var(--nx-surface-alt)',
        position: 'relative', transition: 'border-color 0.15s, background 0.15s',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <NexusIcon name={isBlank ? 'Square' : 'Layout'} size={14}
          style={{ color: isSelected ? 'var(--nx-accent)' : 'var(--nx-text-muted)', flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3,
          color: isSelected ? 'var(--nx-accent)' : 'var(--nx-text)' }}>{cardName}</span>
        {!isBlank && template.isDefault && (
          <NexusIcon name="Star" size={11}
            style={{ color: 'var(--nx-accent)', marginLeft: 'auto', flexShrink: 0 }} />
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', lineHeight: 1.4,
        overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {cardDesc}
      </div>
      {isSelected && (
        <div style={{ position: 'absolute', top: 8, right: 8, width: 16, height: 16, borderRadius: 8,
          background: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NexusIcon name="Check" size={9} style={{ color: '#fff' }} />
        </div>
      )}
    </button>
  );
}

/* ── Wizard: header ─────────────────────────────────────────── */

const STEP_TITLES = ['Workspace Information', 'Configuration Preview', 'Optional Overrides'];

function WizardHeader({ step, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '18px 24px', borderBottom: '1px solid var(--nx-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-accent-tint)',
          color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NexusIcon name="LayoutGrid" size={15} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Create Workspace</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
            Step {step} of 3 — {STEP_TITLES[step - 1]}
          </div>
        </div>
      </div>
      <button type="button" onClick={onClose}
        style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--nx-text-muted)', padding: 4, borderRadius: 4 }}>
        <NexusIcon name="X" size={18} />
      </button>
    </div>
  );
}

/* ── Wizard: step progress bar ──────────────────────────────── */

function WizardStepper({ step }) {
  const steps = ['Info', 'Preview', 'Overrides'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 24px',
      borderBottom: '1px solid var(--nx-border)' }}>
      {steps.map((label, i) => {
        const n      = i + 1;
        const done   = n < step;
        const active = n === step;
        let dotBg, dotBdr, col;
        if (done)        { dotBg = 'var(--nx-success)'; dotBdr = 'var(--nx-success)'; col = 'var(--nx-success)'; }
        else if (active) { dotBg = 'var(--nx-accent)'; dotBdr = 'var(--nx-accent)'; col = 'var(--nx-accent)'; }
        else             { dotBg = 'transparent'; dotBdr = 'var(--nx-text-muted)'; col = 'var(--nx-text-muted)'; }
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 22, height: 22, borderRadius: 11, border: `2px solid ${dotBdr}`,
                background: dotBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {done
                  ? <NexusIcon name="Check" size={10} style={{ color: '#fff' }} />
                  : <span style={{ fontSize: 10, fontWeight: 700,
                      color: active ? '#fff' : 'var(--nx-text-muted)' }}>{n}</span>}
              </div>
              <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: col }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, margin: '0 10px',
                background: n < step ? 'var(--nx-success)' : 'var(--nx-border)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Wizard: step 1 — info + template picker ────────────────── */

function WizardStep1({ name, onNameChange, description, onDescChange,
  templateId, templates, templatesLoading, onSelectTemplate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="nx-form-row">
        <label htmlFor="wiz-name">
          Workspace Name <span style={{ color: 'var(--nx-danger)' }}>*</span>
        </label>
        <input id="wiz-name" className="nx-input" autoFocus
          value={name} onChange={e => onNameChange(e.target.value)}
          placeholder="e.g. Retail Lending" maxLength={120} />
      </div>
      <div className="nx-form-row">
        <label htmlFor="wiz-desc">Description</label>
        <textarea id="wiz-desc" className="nx-input"
          style={{ height: 60, resize: 'vertical' }}
          value={description} onChange={e => onDescChange(e.target.value)}
          placeholder="Optional workspace description" maxLength={300} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Template</div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
          Choose a template to pre-configure this workspace. Applied at creation only — no ongoing sync.
        </div>
        {templatesLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            {[0, 1, 2].map(i => (
              <div key={i} className="nx-cc-skeleton" style={{ height: 76, borderRadius: 10 }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            <TemplateCard template={null} isSelected={templateId === null}
              onSelect={() => onSelectTemplate(null)} />
            {templates.map(t => (
              <TemplateCard key={t.id} template={t} isSelected={templateId === t.id}
                onSelect={() => onSelectTemplate(t.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Wizard: preview helpers ────────────────────────────────── */

function PreviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid var(--nx-border)' }}>
      <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12 }}>{value}</span>
    </div>
  );
}

function PreviewSection({ icon, title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
        color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
        <NexusIcon name={icon} size={11} />{title}
      </div>
      {children}
    </div>
  );
}

/* ── Wizard: step 2 — template preview ─────────────────────── */

function WizardStep2({ templateId, template }) {
  const cfg     = template?.config ?? {};
  const isBlank = templateId === null;
  const mods    = Array.isArray(cfg.modules?.enabled) ? cfg.modules.enabled : DEFAULT_MODULES;
  const sec     = cfg.security  ?? {};
  const ai      = cfg.ai        ?? {};
  const dev     = cfg.developer ?? {};
  const gb      = cfg.storage?.defaultAllocationGb ?? 10;

  return (
    <div>
      {isBlank ? (
        <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 20,
          background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Default Configuration</div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
            Provisioned with platform defaults: standard CRM modules, UTC timezone, and org-inherited security.
          </div>
        </div>
      ) : (
        <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 20,
          background: 'var(--nx-accent-tint)', border: '1px solid var(--nx-accent)',
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Layout" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-accent)' }}>{template?.name}</div>
            {template?.description && (
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{template.description}</div>
            )}
          </div>
        </div>
      )}
      <PreviewSection icon="LayoutGrid" title="Workspace Defaults">
        <PreviewRow label="Timezone" value={cfg.workspace?.timezone || 'UTC'} />
        <PreviewRow label="Locale"   value={cfg.workspace?.locale   || 'en-US'} />
        {cfg.workspace?.namePattern && <PreviewRow label="Name Pattern" value={cfg.workspace.namePattern} />}
      </PreviewSection>
      <PreviewSection icon="Grid" title="CRM Modules">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}>
          {mods.map(m => <Pill key={m} tone="blue">{m}</Pill>)}
        </div>
      </PreviewSection>
      <PreviewSection icon="Shield" title="Security">
        <PreviewRow label="IP Policy"  value={sec.inheritIpPolicy  !== false ? 'Inherit from org' : 'Custom'} />
        <PreviewRow label="MFA Policy" value={sec.inheritMfaPolicy !== false ? 'Inherit from org' : 'Custom'} />
      </PreviewSection>
      <PreviewSection icon="Sparkles" title="AI Platform">
        <PreviewRow label="Status" value={ai.enabled ? 'Enabled' : 'Disabled'} />
      </PreviewSection>
      <PreviewSection icon="Code2" title="Developer">
        <PreviewRow label="API Access" value={dev.enableApiAccess ? 'Enabled' : 'Disabled'} />
        <PreviewRow label="Webhooks"   value={dev.enableWebhooks  ? 'Enabled' : 'Disabled'} />
      </PreviewSection>
      <PreviewSection icon="HardDrive" title="Storage">
        <PreviewRow label="Default Allocation" value={gb + ' GB'} />
      </PreviewSection>
    </div>
  );
}

/* ── Wizard: step 3 — overrides ─────────────────────────────── */

function WizardStep3({ overrides, onChange, template }) {
  const cfg = template?.config ?? {};
  const tz  = cfg.workspace?.timezone             ?? 'UTC';
  const loc = cfg.workspace?.locale               ?? 'en-US';
  const gb  = cfg.storage?.defaultAllocationGb    ?? 10;

  const toggleModule = (mod) => {
    if (overrides.modules.includes(mod)) {
      onChange(o => ({ ...o, modules: o.modules.filter(x => x !== mod) }));
    } else {
      const modDef = MODULE_CATALOG.find(m => m.id === mod);
      const depsToAdd = (modDef?.deps ?? []).filter(dep => !overrides.modules.includes(dep));
      onChange(o => ({ ...o, modules: [...o.modules, mod, ...depsToAdd] }));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8,
        border: '1px solid var(--nx-border)', fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
        Override specific settings for this workspace. Storage, timezone, and locale are managed at the template level.
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>CRM Modules</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {MODULE_CATALOG.map(mod => (
            <ModuleChip key={mod.id} m={mod.id} active={overrides.modules.includes(mod.id)} onToggle={toggleModule} />
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Storage</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{gb} GB</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-faint)', marginTop: 2 }}>From template</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Timezone</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{tz}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-faint)', marginTop: 2 }}>From template</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Locale</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{loc}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-faint)', marginTop: 2 }}>From template</div>
        </div>
      </div>
    </div>
  );
}

/* ── Wizard: shell ──────────────────────────────────────────── */

function ProvisioningWizard({ orgId, wsLimitFull, onClose, onCreated }) {
  const toast = useToast();

  const [step, setStep]             = useState(1);
  const [name, setName]             = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const [template, setTemplate]     = useState(null);
  const [templates, setTemplates]   = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [overrides, setOverrides]   = useState({ modules: [...DEFAULT_MODULES] });
  const [busy, setBusy]             = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res  = await templatesApi.list(orgId, { status: 'ACTIVE', limit: 100 });
        const list = res?.data ?? [];
        if (!alive) return;
        setTemplates(list);
        const def = list.find(t => t.isDefault);
        if (def) {
          setTemplateId(def.id);
          setTemplate(def);
          setOverrides(initOverrides(def));
        }
      } catch { /* fall back to blank workspace */ }
      finally { if (alive) setTplLoading(false); }
    })();
    return () => { alive = false; };
  }, [orgId]);

  const handleSelectTemplate = (tplId) => {
    const tpl = tplId ? templates.find(t => t.id === tplId) ?? null : null;
    setTemplateId(tplId);
    setTemplate(tpl);
    setOverrides(initOverrides(tpl));
  };

  const handleCreate = useCallback(async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const payload = {
        name:    name.trim(),
        modules: overrides.modules,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(templateId         ? { templateId }                      : {}),
      };
      const ws = await orgApi.createWorkspace(orgId, payload);
      toast({ tone: 'success', icon: 'Check', title: 'Workspace created', desc: name.trim() });
      onCreated(ws);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Create failed', desc: err?.message });
    } finally { setBusy(false); }
  }, [orgId, name, description, templateId, overrides, busy, toast, onCreated]);

  const canNext = step === 1 ? Boolean(name.trim()) : true;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button type="button" aria-label="Close dialog" tabIndex={-1} onClick={onClose}
        style={{ position: 'absolute', inset: 0, border: 'none', cursor: 'default',
          background: 'rgba(0,0,0,0.45)', width: '100%', height: '100%' }} />
      <div style={{ position: 'relative', zIndex: 1, background: 'var(--nx-bg)', borderRadius: 12,
          width: 680, maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <WizardHeader step={step} onClose={onClose} />
        <WizardStepper step={step} />
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {step === 1 && (
            <WizardStep1
              name={name} onNameChange={setName}
              description={description} onDescChange={setDescription}
              templateId={templateId} templates={templates}
              templatesLoading={tplLoading}
              onSelectTemplate={handleSelectTemplate}
            />
          )}
          {step === 2 && <WizardStep2 templateId={templateId} template={template} />}
          {step === 3 && <WizardStep3 overrides={overrides} onChange={setOverrides} template={template} />}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 24px', borderTop: '1px solid var(--nx-border)', gap: 8 }}>
          <Btn variant="ghost" icon="X" onClick={onClose}>Cancel</Btn>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 1 && (
              <Btn variant="ghost" icon="ChevronLeft" onClick={() => setStep(s => s - 1)}>Back</Btn>
            )}
            {step < 3 && (
              <Btn variant="primary" icon="ChevronRight" disabled={!canNext}
                onClick={() => setStep(s => s + 1)}>
                Next
              </Btn>
            )}
            {step === 3 && (
              <Btn variant="primary" icon="Check"
                disabled={busy || !name.trim() || wsLimitFull}
                onClick={handleCreate}>
                {busy ? 'Creating…' : 'Create Workspace'}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
function WorkspaceListPanel() {
  const { orgId }   = useParams();
  const navigate    = useNavigate();
  const toast       = useToast();
  const org         = useOrganization();
  const limits      = useLimits();
  const usage       = useUsage();
  const permissions = useOrgPermissions();
  const { invalidateRuntime } = useOrgRuntime();

  const [workspaces,    setWorkspaces]    = useState([]);
  const [pagination,    setPagination]    = useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [serverStats,   setServerStats]   = useState({ active: 0, archived: 0 });
  const [loading,       setLoading]       = useState(true);
  const [wsError,       setWsError]       = useState(null);
  const [page,       setPage]       = useState(1);
  const [q,          setQ]          = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [statusF,    setStatusF]    = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [archiving,  setArchiving]  = useState(null);
  const [busy,       setBusy]       = useState(false);
  const canManage   = Boolean(permissions.canManageWorkspaces);
  const maxWs       = limits.maxWorkspaces ?? 0;
  const usedWs      = usage.workspaces ?? 0;
  const wsLimitFull = maxWs > 0 && usedWs >= maxWs;

  /* Debounce search input → qDebounced */
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQDebounced(q);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadStats = useCallback(async () => {
    if (!orgId) return;
    try {
      const [activeRes, archivedRes] = await Promise.all([
        orgApi.listWorkspaces(orgId, { status: 'ACTIVE',   pageSize: 1, page: 1 }),
        orgApi.listWorkspaces(orgId, { status: 'ARCHIVED', pageSize: 1, page: 1 }),
      ]);
      setServerStats({
        active:   activeRes?.pagination?.total   ?? 0,
        archived: archivedRes?.pagination?.total ?? 0,
      });
    } catch { /* non-critical */ }
  }, [orgId]);

  const loadWorkspaces = useCallback(async (search, status, pg) => {
    if (!orgId) return;
    setLoading(true);
    setWsError(null);
    try {
      const params = { page: pg, pageSize: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await orgApi.listWorkspaces(orgId, params);
      setWorkspaces(Array.isArray(res) ? res : (res?.data ?? []));
      if (res?.pagination) setPagination(res.pagination);
    } catch (err) {
      setWsError(err?.message || 'Failed to load workspaces');
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    loadWorkspaces(qDebounced, statusF, page);
  }, [loadWorkspaces, qDebounced, statusF, page]);

  /* ── Wizard created callback ────────────────────────── */
  const handleWizardCreated = useCallback(() => {
    setWizardOpen(false);
    invalidateRuntime(orgId);
    loadStats();
    loadWorkspaces(qDebounced, statusF, page);
  }, [orgId, qDebounced, statusF, page, invalidateRuntime, loadStats, loadWorkspaces]);

  /* ── Edit ────────────────────────────────────────────── */
  const handleEdit = async () => {
    if (!editing.name.trim()) return;
    setBusy(true);
    try {
      await orgApi.updateWorkspace(orgId, editing.id, { name: editing.name.trim() });
      toast({ tone: 'success', icon: 'Check', title: 'Workspace updated', desc: editing.name.trim() });
      setEditing(null);
      loadWorkspaces(qDebounced, statusF, page);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err?.message });
    } finally { setBusy(false); }
  };

  /* ── Archive ─────────────────────────────────────────── */
  const handleArchive = async () => {
    if (!archiving) return;
    setBusy(true);
    try {
      await orgApi.archiveWorkspace(orgId, archiving.id);
      toast({ tone: 'warning', icon: 'Archive', title: 'Workspace archived', desc: archiving.name });
      setArchiving(null);
      invalidateRuntime(orgId);
      loadStats();
      loadWorkspaces(qDebounced, statusF, page);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: err?.message });
    } finally { setBusy(false); }
  };

  const activeCount   = serverStats.active;
  const archivedCount = serverStats.archived;
  const totalMembers  = workspaces.reduce((s, w) => s + (w._count?.memberships ?? w.memberCount ?? 0), 0);
  const totalCount    = pagination.total || workspaces.length;
  const wsSuffix      = totalCount === 1 ? '' : 's';
  const orgName       = org?.name ?? '…';

  const openCreate = () => setWizardOpen(true);
  const emptyMsg   = (q || statusF) ? 'No workspaces match your filters.' : 'No workspaces yet.';

  return (
    <>
      <PageHead
        title="Workspaces"
        sub={totalCount + ' workspace' + wsSuffix + ' in ' + orgName}
        actions={
          canManage && (
            <Btn
              variant="primary"
              icon="Plus"
              disabled={wsLimitFull}
              title={wsLimitFull ? 'Workspace limit reached (' + maxWs + ')' : undefined}
              onClick={openCreate}
            >
              Create Workspace
            </Btn>
          )
        }
      />

      {/* Stats */}
      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard label="Total"    value={totalCount}    icon="LayoutGrid" color="blue"   />
        <StatCard label="Active"   value={activeCount}   icon="Check"      color="green"  />
        <StatCard label="Archived" value={archivedCount} icon="Archive"    color="gray"   />
        <StatCard label="Members"  value={totalMembers}  icon="Users"      color="purple" />
      </div>

      <LimitBanner used={usedWs} max={maxWs} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '0 0 280px' }}>
          <NexusIcon name="Search" size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
          <input className="nx-input" style={{ paddingLeft: 32, width: '100%' }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search workspaces…" />
        </div>
        <select
          className="nx-input"
          style={{ width: 160, fontSize: 13 }}
          value={statusF}
          onChange={e => { setStatusF(e.target.value); setPage(1); }}
        >
          {STATUS_OPTS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={() => loadWorkspaces(qDebounced, statusF, page)}>
          Refresh
        </Btn>
      </div>

      {/* List */}
      {loading && <TableSkeleton />}
      {!loading && wsError && (
        <div className="nx-empty">
          <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
          <span style={{ color: 'var(--nx-danger)' }}>{wsError}</span>
          <Btn variant="secondary" icon="RefreshCw" onClick={() => loadWorkspaces(qDebounced, statusF, page)}>Retry</Btn>
        </div>
      )}
      {!loading && !wsError && workspaces.length === 0 && (
        <div className="nx-empty">
          <NexusIcon name="LayoutGrid" size={24} />
          <span>{emptyMsg}</span>
          {!q && !statusF && canManage && !wsLimitFull && (
            <Btn variant="secondary" icon="Plus" onClick={openCreate}>Create first workspace</Btn>
          )}
        </div>
      )}
      {!loading && !wsError && workspaces.length > 0 && (
        <>
          <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
            <thead><tr>
              <Th icon="LayoutGrid">Workspace</Th>
              <Th icon="Activity">Status</Th>
              <Th icon="Users">Members</Th>
              <Th icon="Grid">Modules</Th>
              <Th icon="Calendar">Created</Th>
              <Th end />
            </tr></thead>
            <tbody>{workspaces.map(w => (
              <tr
                key={w.id}
                className="nx-tr"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/organization/' + orgId + '/workspaces/' + w.id)}
              >
                <td>
                  <span className="nx-row-name">
                    <span className="nx-domain-mark" style={{ width: 28, height: 28, fontSize: 11, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}>
                      {(w.name || 'W')[0].toUpperCase()}
                    </span>
                    <span className="nx-row-name-text">{w.name}</span>
                  </span>
                </td>
                <td>
                  <Pill tone={WS_STATUS_TONE[w.status] ?? 'gray'}>
                    {WS_STATUS_LABEL[w.status] ?? w.status}
                  </Pill>
                </td>
                <td><span className="nx-muted">{w._count?.memberships ?? w.memberCount ?? 0}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(w.modules ?? []).slice(0, 3).map(m => <Pill key={m} tone="gray">{m}</Pill>)}
                    {(w.modules ?? []).length > 3 && (
                      <span className="nx-muted nx-mini">+{(w.modules ?? []).length - 3}</span>
                    )}
                  </div>
                </td>
                <td><span className="nx-muted nx-mini">{fmt(w.createdAt)}</span></td>
                <td className="nx-td-end" style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                  <Btn variant="ghost" size="sm" icon="Eye"
                    onClick={() => navigate('/organization/' + orgId + '/workspaces/' + w.id)}>
                    View
                  </Btn>
                  {canManage && (
                    <>
                      <Btn variant="ghost" size="sm" icon="Pencil"
                        onClick={() => setEditing({ id: w.id, name: w.name })}>
                        Edit
                      </Btn>
                      {w.status !== 'ARCHIVED' && (
                        <Btn variant="ghost" size="sm" icon="Archive"
                          onClick={() => setArchiving(w)}>
                          Archive
                        </Btn>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table></div></div>

          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
              <span className="nx-muted nx-mini">Page {page} of {pagination.totalPages}</span>
              <Btn variant="ghost" size="sm" icon="ChevronLeft" disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}>Prev</Btn>
              <Btn variant="ghost" size="sm" icon="ChevronRight" disabled={page >= pagination.totalPages}
                onClick={() => setPage(p => p + 1)}>Next</Btn>
            </div>
          )}
        </>
      )}

      {/* ── Provisioning Wizard ──────────────────────────── */}
      {wizardOpen && (
        <ProvisioningWizard
          orgId={orgId}
          wsLimitFull={wsLimitFull}
          onClose={() => setWizardOpen(false)}
          onCreated={handleWizardCreated}
        />
      )}

      {/* ── Edit Modal ───────────────────────────────────── */}
      {editing && (
        <Modal
          title="Edit Workspace"
          icon="Pencil"
          onClose={() => setEditing(null)}
          footer={<>
            <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={busy || !editing.name.trim()} onClick={handleEdit}>
              {busy ? 'Saving…' : 'Save Changes'}
            </Btn>
          </>}
        >
          <div className="nx-form" style={{ gap: 10 }}>
            <div className="nx-form-row">
              <label htmlFor="ws-edit-name">Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input
                id="ws-edit-name"
                className="nx-input"
                autoFocus
                value={editing.name}
                onChange={e => setEditing(c => ({ ...c, name: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleEdit(); }}
                placeholder="Workspace name"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ── Archive Confirm ───────────────────────────────── */}
      {archiving && (
        <ConfirmDialog
          title={'Archive — ' + archiving.name}
          desc="Archive this workspace? It becomes read-only and is hidden from active views. An Org Admin can restore it from the Control Center."
          danger
          busy={busy}
          confirmLabel="Archive Workspace"
          onConfirm={handleArchive}
          onCancel={() => setArchiving(null)}
        />
      )}
    </>
  );
}

export default function Workspaces() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'workspaces';
  const handleTabChange = useCallback((id) => {
    setSearchParams(id === 'workspaces' ? {} : { tab: id });
  }, [setSearchParams]);

  return (
    <div className="nx-cc-page">
      <TabBar tabs={MODULE_TABS} active={activeTab} onChange={handleTabChange} />
      {activeTab === 'templates'
        ? <OrgTemplatesEmbeddable />
        : <WorkspaceListPanel />
      }
    </div>
  );
}
